export type RiskType = 'INTERNAL' | 'EXTERNAL';
export type RiskStatus = 'OPEN' | 'CLOSED' | 'MITIGATED';
export type RiskPriority = 'HIGH' | 'MEDIUM' | 'LOW';

export interface RiskUser {
  userId: number;
  firstName: string;
  lastName: string;
  [key: string]: any;
}

export interface RiskAttachment {
  fileNameOriginal: string;
  url: string;
  [key: string]: any;
}

export interface RiskTimeFrame {
  riskTimeFrameId: number | null;
  code: string;
  name: string;
  createTime?: string;
  updateTime?: string;
}

export interface RiskActionPropose {
  riskActionProposeId: number | null;
  actionPropose: string;
  responsibleUser: RiskUser | null;
  riskTimeFrame: RiskTimeFrame | null;
  lastEvaluation: string | null;
  nextReview: string | null;
  comment: string;
  attachments: RiskAttachment[];
  createTime?: string;
  updateTime?: string;
}

export interface Risk {
  riskId: number | null;
  code: string;
  type: RiskType | null;
  date: string | null;
  description: string;
  creatorUser: RiskUser | null;
  status: RiskStatus | null;
  nextReview: string | null;
  opportunity: string;
  threat: string;
  priority: RiskPriority | null;
  actionProposes: RiskActionPropose[];
  createTime?: string;
  updateTime?: string;
}

export interface RiskActionProposePayload {
  riskActionProposeId: number | null;
  actionPropose: string;
  responsibleUserId: number;
  riskTimeFrameId: number | null;
  lastEvaluation: string | null;
  nextReview: string | null;
  comment: string;
}

export interface RiskPayload {
  type: RiskType;
  date: string | null;
  description: string;
  status: RiskStatus;
  nextReview: string | null;
  opportunity: string;
  threat: string;
  priority: RiskPriority;
  actionProposes: RiskActionProposePayload[];
}

export interface RiskTimeFramePayload {
  code: string;
  name: string;
}

export const RISK_TYPE_LABELS: Record<RiskType, string> = {
  INTERNAL: 'Internal',
  EXTERNAL: 'External'
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  OPEN: 'Open',
  CLOSED: 'Closed',
  MITIGATED: 'Mitigated'
};

export const RISK_PRIORITY_LABELS: Record<RiskPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

export const RISK_STATUS_BADGES: Record<RiskStatus, string> = {
  OPEN: 'bg-success',
  MITIGATED: 'bg-warning',
  CLOSED: 'bg-secondary'
};

export const RISK_PRIORITY_BADGES: Record<RiskPriority, string> = {
  HIGH: 'bg-danger',
  MEDIUM: 'bg-warning',
  LOW: 'bg-success'
};

export const RISK_ERROR_MESSAGES: Record<string, string> = {
  RISK_NOT_FOUND: 'Risk not found.',
  RISK_TIME_FRAME_NOT_FOUND: 'Risk time frame not found.',
  RISK_ACTION_PROPOSE_NOT_FOUND: 'Risk action propose not found.'
};

export function riskUserFullName(user?: RiskUser | null): string {
  if (!user) {
    return '';
  }
  return `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim();
}

export function riskTypeLabel(type?: RiskType | null): string {
  return type ? RISK_TYPE_LABELS[type] ?? type : '';
}

export function riskStatusLabel(status?: RiskStatus | null): string {
  return status ? RISK_STATUS_LABELS[status] ?? status : '';
}

export function riskPriorityLabel(priority?: RiskPriority | null): string {
  return priority ? RISK_PRIORITY_LABELS[priority] ?? priority : '';
}

export function riskActionProposeCurrentAttachment(
  actionPropose?: RiskActionPropose | null
): RiskAttachment | null {
  const attachments = actionPropose?.attachments;

  if (!attachments?.length) {
    return null;
  }

  return attachments[attachments.length - 1];
}
